import { fetchAuthSession } from '../util/amplifyAuth';
import { getCachedFiles, cacheFiles, clearFileCache } from '../util/FileCacheUtil';
import { setLabDefaults } from '../data/defaultTestData';
import { protocolEraDate } from '../util/ProtocolUtil';

function applyCustomConfig(config) {
    if (config?.labDefaults) setLabDefaults(config.labDefaults);
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

// upload-labtest is fronted by CloudFront for upload bandwidth (TCP slow-start
// over the public internet kills throughput on multipart PDF uploads). Falls
// back to the regional API URL if the env var isn't set.
const UPLOAD_LABTEST_URL =
    import.meta.env.VITE_UPLOAD_LABTEST_URL || `${API_BASE_URL}/upload-labtest`;

// All API endpoints
const agentEndpoint = `${API_BASE_URL}/agent`;
const createPatientEndpoint = `${API_BASE_URL}/doctor/create-patient`;
const deleteEndpoint = `${API_BASE_URL}/delete-file`;
const listDoctorPatientsEndpoint = `${API_BASE_URL}/doctor/patients`;
const listPatientDoctorsEndpoint = `${API_BASE_URL}/patient/doctors`;
const lookupDoctorEndpoint = `${API_BASE_URL}/lookup-doctor`;
const manageAccessEndpoint = `${API_BASE_URL}/manage-access`;
const manageActivationEndpoint = `${API_BASE_URL}/manage-activation`;
const processPdfEndpoint = UPLOAD_LABTEST_URL;
const recommendationsEndpoint = `${API_BASE_URL}/recommendations`;
const retrieveEndpoint = `${API_BASE_URL}/retrieve`;
const uploadEndpoint = `${API_BASE_URL}/upload`;
const processCsvEndpoint = uploadEndpoint;
const processJsonEndpoint = uploadEndpoint;

const userProfileEndpoint = `${API_BASE_URL}/user-profile`;
const assessmentEndpoint = `${API_BASE_URL}/save-assessment`;
const checkoutEndpoint = `${API_BASE_URL}/create-checkout-session`;
const subscriptionEndpoint = `${API_BASE_URL}/manage-subscription`;

// Plan/price constants live in one place — src/util/plans.js (env-sourced).
// Re-exported here so existing `import { PLAN_NAMES } from '../components/API'`
// call sites keep working. See xdesign_docs/billing.md §1, §8.
export { PLAN_NAMES, SWITCHABLE_PLANS, PLAN_RANKS, PLAN_CATALOG, TRIAL_DAYS, TRIAL_PLAN } from '../util/plans';

const endpoints = {
    pdf: processPdfEndpoint,
    csv: processCsvEndpoint,
    json: processJsonEndpoint
};

/**
 * Shared helper: fetch auth token, make request, parse JSON response.
 * Supports JSON bodies (auto-serialized) and FormData (passed through).
 * On error, throws with .status and .data attached for caller inspection.
 */
async function authenticatedFetch(endpoint, { method = 'POST', body = null, extraHeaders = {} } = {}) {
    let access_token, userId;
    for (let i = 0; i < 5; i++) {
        const session = await fetchAuthSession({ forceRefresh: i > 0 });
        access_token = session.tokens?.accessToken?.toString();
        if (access_token) {
            userId = session.tokens?.idToken?.payload?.sub;
            break;
        }
        await new Promise(r => setTimeout(r, 1000));
    }
    if (!access_token) throw new Error('Unable to obtain auth session. Please try again.');

    const headers = {
        "authorization": `Bearer ${access_token}`,
        ...(userId && { "x-patient-id": userId }),
        ...extraHeaders
    };
    const fetchOptions = { method, headers };

    if (body != null && !(body instanceof FormData)) {
        headers["Content-Type"] = "application/json";
        fetchOptions.body = JSON.stringify(body);
    } else if (body != null) {
        fetchOptions.body = body;
    }

    const response = await fetch(endpoint, fetchOptions);

    if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        const err = new Error(data.error || data.message || `Request failed: ${response.statusText}`);
        err.status = response.status;
        err.data = data;
        throw err;
    }

    return await response.json();
}

/**
 * Internal function to fetch and cache files for any user
 * @param {string} retrieveId - UUID of user whose files to retrieve (cache key)
 * @param {object} headers - HTTP headers for the request
 * @param {function} setLoadingFiles - Loading state setter
 * @param {function} normalizeTestName - Test name normalizer
 * @param {boolean} skipCache - Force fetch even if cached
 * @returns {Array|null} Processed files or null on error
 */
async function fetchFilesForUser(retrieveId, headers, setLoadingFiles, normalizeTestName, skipCache = false) {
    if (!skipCache) {
        const cached = getCachedFiles(retrieveId);
        if (cached) {
            console.log(`Cache hit for user ${retrieveId}`);
            try {
                const cachedConfig = sessionStorage.getItem('customConfig');
                if (cachedConfig) applyCustomConfig(JSON.parse(cachedConfig));
            } catch {}
            return cached;
        }
    }

    setLoadingFiles(true);
    try {
        const response = await fetch(retrieveEndpoint, {
            method: "GET",
            headers
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error("Error response:", errorText);
            return null;
        }

        let responseData;
        try {
            responseData = await response.json();
        } catch (e) {
            console.error("JSON parse error:", e);
            return null;
        }

        const files = Array.isArray(responseData) ? responseData : responseData?.files;
        const customConfig = Array.isArray(responseData) ? null : responseData?.customConfig;

        if (!Array.isArray(files)) {
            console.error("Files is not an array:", files);
            return null;
        }

        if (customConfig) {
            applyCustomConfig(customConfig);
            try { sessionStorage.setItem('customConfig', JSON.stringify(customConfig)); } catch {}
        }

        const processedFiles = files.map(file => {
            let parsedData = JSON.parse(file.data);

            if (parsedData.body && Array.isArray(parsedData.body)) {
                parsedData.body = parsedData.body.map(entry => ({
                    ...entry,
                    alias: normalizeTestName(entry.test_name, entry)
                }));
            }

            return { key: file.key, lastModified: file.lastModified ?? null, data: parsedData };
        });

        // Cache the processed files
        cacheFiles(retrieveId, processedFiles);
        console.log(`Cached files for user ${retrieveId}`);

        return processedFiles;
    } catch (error) {
        console.error("Error retrieving files:", error);
        return null;
    } finally {
        setLoadingFiles(false);
    }
}

/**
 * Retrieve files for the current logged-in user
 */
export const retrieveUserFiles = async (setLoadingFiles, normalizeTestName, skipCache = false) => {
    const session = await fetchAuthSession();
    const access_token = session.tokens?.accessToken?.toString();
    if (!access_token) return null;

    // Get current user's sub (UUID) as the cache key
    const userId = session.tokens?.idToken?.payload?.sub;
    if (!userId) {
        console.error("Could not get user ID from session");
        return null;
    }

    const headers = { "authorization": `Bearer ${access_token}`, "x-patient-id": userId };
    return fetchFilesForUser(userId, headers, setLoadingFiles, normalizeTestName, skipCache);
};

/**
 * Retrieve files for a specific patient (doctor access)
 * @param {string} patientId - Patient UUID to retrieve files for
 */
export const retrievePatientFiles = async (patientId, setLoadingFiles, normalizeTestName, skipCache = false) => {
    const session = await fetchAuthSession();
    const access_token = session.tokens?.accessToken?.toString();
    if (!access_token) return null;

    // patientId is the cache key (whose files we're retrieving)
    const headers = {
        "authorization": `Bearer ${access_token}`,
        "x-patient-id": patientId
    };
    // Always skip cache — scope-filtered results can change server-side
    return fetchFilesForUser(patientId, headers, setLoadingFiles, normalizeTestName, true);
};

/**
 * Retrieve custom config for the current doctor (no x-patient-id).
 */
export const retrieveDoctorConfig = async () => {
    const session = await fetchAuthSession();
    const access_token = session.tokens?.accessToken?.toString();
    if (!access_token) return null;

    try {
        const response = await fetch(retrieveEndpoint, {
            method: "GET",
            headers: { "authorization": `Bearer ${access_token}` }
        });

        if (!response.ok) return null;
        const data = await response.json();
        const config = data.customConfig || null;
        if (config) {
            applyCustomConfig(config);
            try { sessionStorage.setItem('customConfig', JSON.stringify(config)); } catch {}
        }
        return config;
    } catch (e) {
        console.error('Error fetching doctor config:', e);
        return null;
    }
};

/**
 * Invalidate cache for a specific user (call after upload/delete)
 */
export { clearFileCache };

export const uploadFile = async (file, patientId = null) => {
    const fileType = file.name.split(".").pop().toLowerCase();
    const apiEndpoint = endpoints[fileType] || null;
    if (!apiEndpoint) {
        alert("Unsupported file type. Please select a PDF, CSV, or JSON file.");
        return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("filename", file.name);

    const extraHeaders = {};
    if (patientId) extraHeaders["x-patient-id"] = patientId;

    return authenticatedFetch(apiEndpoint, { body: formData, extraHeaders });
};

/**
 * Upload a batch of lab-result screenshots, already shrunk and base64-encoded by
 * prepareImageForUpload. The Lambda reads the batch in one vision pass and
 * answers like a PDF upload: `files` holds one document per draw it found.
 */
export const uploadLabImages = async (images, patientId = null) => {
    const extraHeaders = {};
    if (patientId) extraHeaders["x-patient-id"] = patientId;
    return authenticatedFetch(processPdfEndpoint, {
        body: { images: images.map(({ name, b64 }) => ({ name, b64 })) },
        extraHeaders,
    });
};

export const deleteFile = async (filename, patientId = null) => {
    const extraHeaders = {};
    if (patientId) extraHeaders["x-patient-id"] = patientId;
    return authenticatedFetch(deleteEndpoint, { body: { filename }, extraHeaders });
};

export const getRecommendations = async (labTests, fxRange) => {
    return authenticatedFetch(recommendationsEndpoint, {
        body: { lab_data_list: labTests, fx_data: fxRange }
    });
};

export const lookupDoctor = async (doctorCode) => {
    try {
        return await authenticatedFetch(lookupDoctorEndpoint, { body: { doctorCode } });
    } catch (error) {
        if (error.status === 404) {
            throw new Error("Doctor not found. Please check the code and try again.");
        }
        throw error;
    }
};

export const manageAccess = async (action, doctorId, options = {}) => {
    return authenticatedFetch(manageAccessEndpoint, {
        body: { action, doctorId, ...options }
    });
};

export const listDoctorPatients = async () => {
    return authenticatedFetch(listDoctorPatientsEndpoint, { method: 'GET' });
};

export const createPatient = async (patientData) => {
    try {
        return await authenticatedFetch(createPatientEndpoint, { body: patientData });
    } catch (error) {
        if (error.data?.error) error.code = error.data.error;
        throw error;
    }
};

export const manageActivation = async (action, payload = {}) => {
    try {
        const response = await fetch(manageActivationEndpoint, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ action, ...payload })
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Request failed: ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error(`Error in manage-activation (${action}):`, error);
        throw error;
    }
};

export const sendChatMessage = async (patientId, message, chatId = null, provider = null, model = null) => {
    const session = await fetchAuthSession();
    const access_token = session.tokens.accessToken.toString();
    if (!access_token) return;
    const userId = session.tokens?.idToken?.payload?.sub;

    const headers = {
        "authorization": `Bearer ${access_token}`,
        "x-patient-id": patientId || userId,
        "Content-Type": "application/json"
    };

    const response = await fetch(agentEndpoint, {
        method: "POST",
        headers,
        body: JSON.stringify({
            mode: "chat",
            message,
            ...(chatId && { chatId }),
            ...(provider && { provider }),
            ...(model && { model })
        })
    });

    if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || `Chat request failed: ${response.statusText}`);
    }

    return await response.json();
};

export const fetchModels = async () => {
    const session = await fetchAuthSession();
    const access_token = session.tokens.accessToken.toString();
    if (!access_token) return null;
    const userId = session.tokens?.idToken?.payload?.sub;

    const response = await fetch(agentEndpoint, {
        method: "POST",
        headers: {
            "authorization": `Bearer ${access_token}`,
            "x-patient-id": userId,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ mode: "models" })
    });

    if (!response.ok) return null;
    return await response.json();
};

export const requestInterpretation = async (patientId, fileKey) => {
    const session = await fetchAuthSession();
    const access_token = session.tokens.accessToken.toString();
    if (!access_token) return;

    const response = await fetch(agentEndpoint, {
        method: "POST",
        headers: {
            "authorization": `Bearer ${access_token}`,
            "x-patient-id": patientId,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            mode: "interpret",
            fileKey
        })
    });

    if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || `Interpretation failed: ${response.statusText}`);
    }

    return await response.json();
};

export const lookupUser = async (email) => {
    try {
        return await authenticatedFetch(manageAccessEndpoint, {
            body: { action: 'lookup-user', email }
        });
    } catch (error) {
        if (error.status === 404) {
            throw new Error("No account found with this email");
        }
        throw error;
    }
};

export const generateInvite = async (scope = 'all', allowedResources = null, expirationDays = null) => {
    const body = { action: 'generate-invite', scope };
    if (scope === 'selective' && allowedResources) body.allowedResources = allowedResources;
    if (expirationDays != null) body.expirationDays = expirationDays;
    return authenticatedFetch(manageAccessEndpoint, { body });
};

export const redeemInvite = async (token) => {
    return authenticatedFetch(manageAccessEndpoint, {
        body: { action: 'redeem-invite', token }
    });
};

export const listGrantedToMe = async () => {
    try {
        return await authenticatedFetch(`${listPatientDoctorsEndpoint}?direction=grantee`, { method: 'GET' });
    } catch (error) {
        if (error.status === 404 || error.status === 403) return { patients: [] };
        throw error;
    }
};

/**
 * Save protocol as protocol_<era>.json via the existing upload endpoint.
 * The era date is YYYYMMDD of generatedAt — edits update the same era file in
 * place; a new era file appears only when a new protocol is generated (callers
 * pass a fresh generatedAt on regeneration and preserve it on edits).
 */
export const saveProtocol = async (protocolData, patientId = null) => {
    const session = await fetchAuthSession();
    const access_token = session.tokens?.accessToken?.toString();
    if (!access_token) return;

    const callerId = session.tokens?.idToken?.payload?.sub;

    // Build protocol envelope
    const protocol = {
        type: 'protocol',
        version: 2,
        generatedAt: protocolData.generatedAt || new Date().toISOString(),
        lastEditedAt: new Date().toISOString(),
        generatedBy: {
            userId: callerId,
            userType: patientId ? 'doctor' : 'patient'
        },
        testdateUsed: protocolData.testdateUsed || null,
        bloodTestFingerprint: protocolData.bloodTestFingerprint || null,
        locked: protocolData.locked ?? false,
        lockedByName: protocolData.lockedByName || null,
        patientNotes: protocolData.patientNotes || '',
        doctorNotes: protocolData.doctorNotes || '',
        entries: protocolData.entries || [],
        rawRecommendations: protocolData.rawRecommendations || null
    };

    // Era = LOCAL date of generatedAt (compliance day keys are local; a UTC
    // slice would mint tomorrow's era during local evenings)
    const eraDate = protocolEraDate(protocol.generatedAt);
    const protocolFilename = `protocol_${eraDate}.json`;

    const blob = new Blob([JSON.stringify(protocol)], { type: 'application/json' });
    const file = new File([blob], protocolFilename, { type: 'application/json' });

    const formData = new FormData();
    formData.append('file', file);
    formData.append('filename', protocolFilename);

    const headers = { "authorization": `Bearer ${access_token}` };
    headers["x-patient-id"] = patientId || callerId;

    try {
        const response = await fetch(uploadEndpoint, {
            method: "POST",
            headers,
            body: formData,
        });

        if (!response.ok) {
            throw new Error(`Failed to save protocol: ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error("Error saving protocol:", error);
        throw error;
    }
};

/**
 * Toggle protocol lock via the manage-access endpoint (lock-protocol action).
 */
export const toggleProtocolLock = async (patientId, lock) => {
    return authenticatedFetch(manageAccessEndpoint, {
        body: { action: 'lock-protocol', patientId, lock }
    });
};

export const getUserProfile = async (patientId = null) => {
    const extraHeaders = patientId ? { 'x-patient-id': patientId } : {};
    return authenticatedFetch(userProfileEndpoint, { body: { action: 'get-profile' }, extraHeaders });
};

export const saveUserProfile = async (profileData) => {
    return authenticatedFetch(userProfileEndpoint, { body: { action: 'save-profile', ...profileData } });
};

export const checkProfileComplete = async () => {
    return authenticatedFetch(userProfileEndpoint, { body: { action: 'check-complete' } });
};

export const acceptTerms = async () => {
    return authenticatedFetch(userProfileEndpoint, {
        body: { action: 'accept-terms', termsVersion: '2026-04-27' }
    });
};

export const checkTermsAccepted = async () => {
    return authenticatedFetch(userProfileEndpoint, { body: { action: 'check-terms' } });
};

export const listPatientDoctors = async () => {
    try {
        return await authenticatedFetch(listPatientDoctorsEndpoint, { method: 'GET' });
    } catch {
        return { doctors: [] };
    }
};

export const saveFocuses = async (focuses) => {
    return authenticatedFetch(userProfileEndpoint, { body: { action: 'save-focuses', focuses } });
};

export const checkFocuses = async () => {
    return authenticatedFetch(userProfileEndpoint, { body: { action: 'check-focuses' } });
};

export const saveAssessment = async (assessmentId, answers) => {
    return authenticatedFetch(assessmentEndpoint, {
        body: { action: 'save', assessmentId, answers }
    });
};

export const getAssessmentSummary = async (patientId = null) => {
    const extraHeaders = patientId ? { 'x-patient-id': patientId } : {};
    return authenticatedFetch(assessmentEndpoint, {
        body: { action: 'get-summary' },
        extraHeaders,
    });
};

// ========================================
// Payment / Subscription
// ========================================

export const createCheckoutSession = async (type, { priceId, items, metadata, withTrial, trialConsent } = {}) => {
    return authenticatedFetch(checkoutEndpoint, {
        body: { type, priceId, items, metadata, withTrial, trialConsent }
    });
};

export const getSubscriptionStatus = async () => {
    return authenticatedFetch(subscriptionEndpoint, {
        body: { action: 'get-status' }
    });
};

export const createBillingPortalSession = async () => {
    return authenticatedFetch(subscriptionEndpoint, {
        body: { action: 'create-portal-session' }
    });
};

export const changePlan = async (priceId) => {
    return authenticatedFetch(subscriptionEndpoint, {
        body: { action: 'change-plan', priceId }
    });
};

export const previewPlanChange = async (priceId) => {
    return authenticatedFetch(subscriptionEndpoint, {
        body: { action: 'preview-plan-change', priceId }
    });
};

export const cancelPlanChange = async () => {
    return authenticatedFetch(subscriptionEndpoint, {
        body: { action: 'cancel-plan-change' }
    });
};

export const cancelSubscription = async () => {
    return authenticatedFetch(subscriptionEndpoint, {
        body: { action: 'cancel-subscription' }
    });
};

export const resumeSubscription = async () => {
    return authenticatedFetch(subscriptionEndpoint, {
        body: { action: 'resume-subscription' }
    });
};

export const getOrders = async () => {
    return authenticatedFetch(subscriptionEndpoint, {
        body: { action: 'get-orders' }
    });
};
